import { ArrowRight, Building2, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import type { DonationCampaign } from "../../donation/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  formatInr,
  getAmountStillNeeded,
  getFundingProgress,
  isCampaignLiveStatus,
} from "../../donation/utils";

interface CampaignCardProps {
  campaign: DonationCampaign;
  variant?: "featured" | "category";
}

export default function CampaignCard({
  campaign,
  variant = "featured",
}: CampaignCardProps) {
  const hasGoal = campaign.goal > 0;
  const progress = getFundingProgress(campaign.raised, campaign.goal);
  const stillNeeded = getAmountStillNeeded(campaign.raised, campaign.goal);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-lg">
      <div className="relative h-48 overflow-hidden sm:h-60">
        <img
          src={campaign.image}
          alt={campaign.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/45 to-transparent"
          aria-hidden
        />
        {campaign.dateStatus === "past" ? (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-gray-600/90 px-3 py-1 text-xs font-semibold text-white">
            Ended
          </span>
        ) : campaign.dateStatus === "upcoming" ? (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-[#D4A017] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
            Upcoming
          </span>
        ) : isCampaignLiveStatus(campaign.dateStatus) ||
          (!campaign.dateStatus && campaign.isActive !== false) ? (
          <span className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-[#1B7A3D] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-white"
              aria-hidden
            />
            Live
          </span>
        ) : campaign.featured && variant === "featured" ? (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
            Featured
          </span>
        ) : null}
        {campaign.eventDate && (
          <span className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-[#7A1E1E]">
            <Calendar className="h-3.5 w-3.5" />
            {campaign.eventDate}
          </span>
        )}
        <div className="absolute bottom-3 left-3 right-3 z-10 min-w-0 space-y-1">
          {campaign.institutionName && (
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="flex min-w-0 max-w-full cursor-default items-center gap-1.5 text-xs font-medium text-white/90 drop-shadow-sm">
                  <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="w-[80%] min-w-0 truncate">
                    {campaign.institutionName}
                  </span>
                </p>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="end"
                sideOffset={8}
                arrowClassName="fill-primary bg-primary"
                className="max-w-[min(18rem,calc(100vw-2rem))] rounded-lg border-0 bg-[#333333a8] px-3 py-1.5 text-left text-xs font-medium leading-relaxed text-white shadow-xl backdrop-blur-[2px]"
              >
                {campaign.institutionName}
              </TooltipContent>
            </Tooltip>
          )}
          <h3 className="truncate text-xl font-bold leading-tight text-white drop-shadow-sm">
            {campaign.title}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        {variant === "category" && (
          <p className="mb-3 line-clamp-2 text-sm text-gray-600">
            {campaign.description}
          </p>
        )}

        {hasGoal ? (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2 text-sm">
              <span className="text-lg font-semibold text-[#7A1E1E]">
                {formatInr(campaign.raised)}
              </span>
              <span className="text-gray-500">
                of {formatInr(campaign.goal)}
              </span>
            </div>
            <div className="mb-2 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-[#7A1E1E] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mb-4 flex justify-between text-xs">
              <span className="font-medium text-[#D4A017]">
                {progress}% funded
              </span>
              <span className="text-gray-500">
                {formatInr(stillNeeded)} still needed
              </span>
            </div>
          </>
        ) : (
          <p className="mb-4 line-clamp-2 flex-1 text-sm text-gray-600">
            {campaign.description}
          </p>
        )}

        {import.meta.env.VITE_DISABLE_DONATION === "true" ? (
          <button
            disabled
            className="mt-auto flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-[#7A1E1E] px-4 py-3 text-sm font-semibold text-white opacity-80"
          >
            Donate now
            <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <Link
            to={`/donation/${campaign.id}`}
            className="mt-auto flex w-full items-center justify-center gap-2 rounded-xl bg-[#7A1E1E] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#5A1515]"
          >
            Donate now
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    </article>
  );
}
